/**
 * PlaylistItem Component
 * Individual playlist item with color accent, click handler, and hover prefetch
 */

import { memo, useCallback, useEffect, useState } from 'react';
import { Star, StarOff, Play, GripVertical, ChevronRight, ChevronDown, Loader2, Heart } from 'lucide-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { PlaylistSummary } from '../../api/types';
import { usePrefetchPlaylist, usePlayerControls, usePlaybackState, useDeletePlaylist, usePlaylist, useLikedTracks } from '../../api/hooks';
import { useSetOpsStore } from '../../stores/setops';
import { usePlayerUIStore } from '../../stores/player';
import { useSequencerStore } from '../../stores/sequencer';
import { PlaylistContextMenu } from './PlaylistContextMenu';
import { useWorkbenchStore } from '../../stores/workbench';
import { TrackPreviewCard } from './TrackPreviewCard';

interface PlaylistItemProps {
  playlist: PlaylistSummary;
  isWideMode: boolean;
  isActive: boolean;
  isPinned: boolean;
  isExpanded: boolean;
  onTogglePin: (playlistId: string) => void;
  onToggleExpand: (playlistId: string) => void;
  onClick: (playlist: PlaylistSummary) => void;
}

// Color palette for playlist accent bars
const ACCENT_COLORS = [
  'bg-orange-500',
  'bg-red-500',
  'bg-pink-500',
  'bg-purple-500',
  'bg-blue-500',
  'bg-cyan-500',
  'bg-teal-500',
  'bg-green-500',
  'bg-lime-500',
  'bg-yellow-500',
  'bg-amber-500',
  'bg-rose-500',
] as const;

/**
 * Get consistent accent color based on playlist ID
 */
function getAccentColor(playlistId: string): string {
  const hash = playlistId.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);
  const index = hash % ACCENT_COLORS.length;
  return ACCENT_COLORS[index] ?? 'bg-emerald-500';
}

export const PlaylistItem = memo(function PlaylistItem({
  playlist,
  isWideMode,
  isActive,
  isPinned,
  isExpanded,
  onTogglePin,
  onToggleExpand,
  onClick,
}: PlaylistItemProps) {
  const prefetch = usePrefetchPlaylist();
  const { play } = usePlayerControls();
  const { startDrag, endDrag } = useSetOpsStore();
  const { isPlayerActive, setShowDeviceSelector, setIsPlayerActive, setPendingPlayAction } = usePlayerUIStore();
  const deletePlaylist = useDeletePlaylist();
  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);
  const setActivePlaylist = useWorkbenchStore((state) => state.setActivePlaylist);
  const { isOpen: isSequencerOpen } = useSequencerStore();

  // Get playback state to check if device is available
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Determine if this is Liked Songs
  const isLikedSongs = playlist.id === 'liked-songs';

  // Fetch playlist tracks (for regular playlists)
  const { data: playlistDetail, isLoading: isLoadingPlaylist } = usePlaylist(
    playlist.id,
    { enabled: !isLikedSongs && isWideMode && isExpanded }
  );

  // Fetch liked tracks (for Liked Songs)
  const { data: likedTracksData, isLoading: isLoadingLiked } = useLikedTracks(
    { limit: 50, offset: 0 },
    { enabled: isLikedSongs && isWideMode && isExpanded }
  );

  // Extract tracks based on playlist type
  const tracks = isLikedSongs
    ? (likedTracksData?.tracks ?? [])
    : (playlistDetail?.tracks.items.map(pt => pt.track) ?? []);

  // Determine loading state
  const isLoadingTracks = isLikedSongs ? isLoadingLiked : isLoadingPlaylist;

  // Setup draggable
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `playlist-drag-${playlist.id}`,
    data: { type: 'playlist', playlist },
  });

  // Setup droppable for tracks from workbench
  // Disable for Liked Songs (read-only) and when sequencer is open
  const { setNodeRef: setDroppableRef, isOver: isDropOver } = useDroppable({
    id: `playlist-drop-${playlist.id}`,
    data: { type: 'playlist-drop-zone', playlistId: playlist.id },
    disabled: isLikedSongs || isSequencerOpen,
  });

  // Combine refs
  const setNodeRef = (node: HTMLElement | null) => {
    setDraggableRef(node);
    setDroppableRef(node);
  };

  const handleMouseEnter = useCallback(() => {
    // Prefetch playlist data on hover for instant loading
    prefetch(playlist.id);
  }, [playlist.id, prefetch]);

  const handleClick = useCallback(() => {
    onClick(playlist);
  }, [onClick, playlist]);

  const handleTogglePin = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePin(playlist.id);
  }, [onTogglePin, playlist.id]);

  const handleToggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isWideMode) {
      onToggleExpand(playlist.id);
    }
  }, [isWideMode, onToggleExpand, playlist.id]);

  const handlePlayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Check if device is available before playing
    if (!playbackState?.device) {
      setIsPlayerActive(true); // Activate player
      // Save pending action to execute after device selection
      setPendingPlayAction(() => {
        play.mutate({
          contextUri: playlist.uri,
        });
      });
      setShowDeviceSelector(true);
      return;
    }

    play.mutate({
      contextUri: playlist.uri,
    });
  }, [play, playlist.uri, playbackState, setShowDeviceSelector, setIsPlayerActive, setPendingPlayAction]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleDelete = useCallback((playlistId: string) => {
    // If deleting the currently active playlist, clear the workbench
    if (activePlaylist?.id === playlistId) {
      setActivePlaylist(null);
    }

    deletePlaylist.mutate(playlistId, {
      onError: (error) => {
        console.error('Failed to delete playlist:', error);
        // Could show a toast notification here
      },
    });
  }, [deletePlaylist, activePlaylist, setActivePlaylist]);

  // Notify store on drag state changes
  useEffect(() => {
    if (isDragging) {
      startDrag(playlist);
    } else {
      endDrag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, playlist.id]);


  const accentColor = getAccentColor(playlist.id);

  // Apply transform styles when dragging
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style}>
      {/* Main playlist row */}
      <div
        className={`
          group relative flex items-center gap-3 p-2.5 rounded-lg
          transition-all duration-150
          border border-transparent
          ${isDragging ? 'opacity-50' : ''}
          ${isDropOver && !isLikedSongs && !isSequencerOpen
            ? 'bg-emerald-500/20 border-emerald-500/50 ring-2 ring-emerald-500/30'
            : ''
          }
          ${isActive
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'hover:bg-zinc-800/50 hover:border-zinc-700'
          }
        `}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onContextMenu={handleContextMenu}
      >
        {/* Expand/Collapse Chevron (only in wide mode) */}
        {isWideMode && (
          <button
            onClick={handleToggleExpand}
            className="p-1 rounded hover:bg-zinc-700/50 transition-all flex-shrink-0"
            title={isExpanded ? 'Collapse playlist' : 'Expand playlist'}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            )}
          </button>
        )}

        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className={`
            p-1 rounded text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/50
            transition-all opacity-0 group-hover:opacity-100
            ${isDragging ? 'cursor-grabbing opacity-100!' : 'cursor-grab'}
          `}
          onClick={(e) => e.stopPropagation()}
          title={`Drag to combine playlists\n${playlist.tracksCount} track${playlist.tracksCount !== 1 ? 's' : ''} in this playlist`}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Color accent bar or special icon for Liked Songs */}
        {playlist.id === 'liked-songs' ? (
          <Heart className="w-5 h-5 text-pink-500 fill-current flex-shrink-0" />
        ) : (
          <div className={`w-1 h-10 rounded-full ${accentColor}`} />
        )}

        {/* Playlist info */}
        <div
          className="flex-1 min-w-0"
          title={`${playlist.name}\n${playlist.tracksCount} track${playlist.tracksCount !== 1 ? 's' : ''}\nOwner: ${playlist.owner}\nID: ${playlist.id}`}
        >
          <h3 className={`
            text-xs font-medium truncate
            ${isActive ? 'text-emerald-400' : 'text-zinc-100'}
          `}>
            {playlist.name}
          </h3>
          <p className="text-[10px] text-zinc-500 mt-0.5">
            {playlist.tracksCount} track{playlist.tracksCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Play button */}
        <button
          onClick={handlePlayClick}
          className="p-1.5 rounded transition-all text-emerald-400 opacity-0 group-hover:opacity-100 hover:text-emerald-300 hover:bg-emerald-500/10"
          title={`Play playlist\n${playlist.uri}`}
        >
          <Play className="w-4 h-4 fill-current" />
        </button>

        {/* Pin button */}
        <button
          onClick={handleTogglePin}
          className={`
            p-1.5 rounded transition-all
            ${isPinned
              ? 'text-amber-400 hover:text-amber-300'
              : 'text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-zinc-400'
            }
          `}
          title={isPinned
            ? 'Unpin from favorites\nPinned playlists appear at the top'
            : 'Pin to favorites\nPinned playlists appear at the top'
          }
        >
          {isPinned ? (
            <Star className="w-4 h-4 fill-current" />
          ) : (
            <StarOff className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Expanded Track List (only in wide mode when expanded) */}
      {isWideMode && isExpanded && (
        <div className="pl-4 pr-2 pb-2 space-y-1 max-h-[500px] overflow-y-auto">
          {isLoadingTracks ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
            </div>
          ) : tracks.length > 0 ? (
            tracks.map((track) => (
              <TrackPreviewCard
                key={track.id}
                track={track}
                playlistId={playlist.id}
              />
            ))
          ) : (
            <div className="text-xs text-zinc-500 text-center py-4">
              Empty playlist
            </div>
          )}
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <PlaylistContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          playlistId={playlist.id}
          playlistName={playlist.name}
          playlistUri={playlist.uri}
          onClose={handleCloseContextMenu}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
});
